import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Mail, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Backend always returns a generic message (no account enumeration).
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fc-hero-bg fc-paper-noise flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <div className="mb-6"><Wordmark withTagline /></div>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="w-full max-w-md">
        <Card className="border-border bg-card shadow-[0_18px_50px_rgba(20,20,20,0.10)]">
          <CardContent className="p-7">
            {sent ? (
              <div className="flex flex-col items-center gap-4 py-4 text-center" data-testid="forgot-password-sent">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <MailCheck className="h-7 w-7 text-primary" />
                </div>
                <h1 className="font-serif text-2xl font-semibold text-foreground">Check your inbox</h1>
                <p className="text-sm text-muted-foreground">
                  If an account exists for <strong className="text-foreground">{email}</strong>, we&rsquo;ve sent a reset link. It expires in 60 minutes &mdash; remember to check your spam folder.
                </p>
                <div className="mt-2 flex w-full flex-col gap-2">
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="forgot-password-back-to-login">
                    <Link to="/login">Back to sign in</Link>
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => setSent(false)} data-testid="forgot-password-try-again">
                    Use a different email
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="font-serif text-2xl font-semibold text-foreground">Forgot your password?</h1>
                <p className="mt-1 text-sm text-muted-foreground">Enter the email on your account and we&rsquo;ll send a link to reset it.</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" data-testid="forgot-password-email-input" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="forgot-password-submit-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-2 h-4 w-4" /> Send reset link</>}
                  </Button>
                </form>

                <p className="mt-6 text-center text-sm text-muted-foreground">
                  <Link to="/login" className="inline-flex items-center font-semibold text-primary hover:underline" data-testid="forgot-password-back-link">
                    <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back to sign in
                  </Link>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
